import type { MessageType } from "@expert/shared-types";

import { Container, Stack } from "@mantine/core";

import type { MessageTypeConfigRuntime } from "../../config/types";
import type { ExpertAssistMessage, ExpertAssistMetadata } from "../../shared-types";

import { MessageBubbleHeader, MessageBubbleWrapper } from "../message-bubble";
import { MessageBubbleSimplified } from "../message-bubble/MessageBubbleSimplified";
import { QuickActionGroup, type QuickActionGroupProps } from "../quick-actions";
import { RetryButton } from "../retry-button";
import { MessageTag, TextMask } from "./elements";
import { MessageActions } from "./MessageActions";
import timelineClasses from "./TimelineItem.module.css";

interface MessageContentSimplifiedProps {
    type: MessageType;
    config: MessageTypeConfigRuntime;
    metadata?: ExpertAssistMetadata;
    isLastProactiveMessage: boolean;
    overflowRef: React.RefObject<HTMLDivElement | null>;
    messageIntersectionRef: (node: HTMLDivElement | null) => void;
    tag?: string;
    message?: ExpertAssistMessage;
    quickActionGroupProps: QuickActionGroupProps;
    isQuickActionsVisible: boolean;
    children: React.ReactNode;
}

export function MessageContentSimplified({
    type,
    config,
    metadata,
    isLastProactiveMessage,
    overflowRef,
    messageIntersectionRef,
    tag,
    message,
    quickActionGroupProps,
    isQuickActionsVisible,
    children,
}: MessageContentSimplifiedProps) {
    const { aiGeneratedContainer, messageBubbleAndHeaderWrapper } = timelineClasses;
    const isAiGenerated = config.bubbleStyle === "aiGenerated";
    const containerClassName = isAiGenerated
        ? `${messageBubbleAndHeaderWrapper} ${aiGeneratedContainer}`
        : messageBubbleAndHeaderWrapper;
    const shouldRenderActionsOutside = type === "SmartOffer";

    const messageBubbleHeader = MessageBubbleHeader({
        isLastProactiveMessage,
        type,
        metadata,
        citations: message?.citations,
    });

    const bubbleContent = (
        <Container className={containerClassName}>
            {messageBubbleHeader}
            <MessageBubbleWrapper
                isBotListening={config.isBotListening}
                isExpanded={config.shouldShowExpanded}
                type={type}
            >
                <MessageBubbleSimplified
                    bubbleStyle={config.bubbleStyle}
                    dataVariant={config.dataVariant}
                    hasHeader={!!messageBubbleHeader}
                    overflowRef={overflowRef}
                    messageIntersectionRef={messageIntersectionRef}
                    type={type}
                >
                    <TextMask isExpanded={config.shouldShowExpanded}>
                        {config.shouldShowMessageTag && tag && <MessageTag tagName={tag} />}
                        {children}
                    </TextMask>
                    <MessageActions
                        shouldRenderActionsOutside={shouldRenderActionsOutside}
                        isQuickActionsVisible={isQuickActionsVisible}
                        quickActionGroupProps={quickActionGroupProps}
                        shouldShowFeedback={config.shouldShowFeedback}
                        message={message}
                    />
                </MessageBubbleSimplified>
                {isQuickActionsVisible && shouldRenderActionsOutside && (
                    <QuickActionGroup {...quickActionGroupProps} quickActionStyle="smartOffer" />
                )}
            </MessageBubbleWrapper>
        </Container>
    );

    if (message?.type === "Expert" && message.metadata?.retryPayload) {
        return (
            <Stack gap={0} w="100%" align="flex-end">
                {bubbleContent}
                <RetryButton
                    message={message}
                    logger={quickActionGroupProps.logger}
                    gaiaPayloadProps={quickActionGroupProps.gaiaPayloadProps}
                />
            </Stack>
        );
    }

    return bubbleContent;
}
